import Vue from "vue";
import {
  camelCase,
  capitalCase,
  constantCase,
  dotCase,
  headerCase,
  noCase,
  paramCase,
  pascalCase,
  pathCase,
  sentenceCase,
  snakeCase,
} from "change-case";
import { plural, singular } from "pluralize";
import { escape as escapeHtml } from "lodash";

Vue.filter("camelCase", function (value) {
  return value ? camelCase(value) : value;
});
Vue.filter("titleCase", function (value) {
  return value ? capitalCase(value) : value;
});
Vue.filter("capitalCase", function (value) {
  return value ? capitalCase(value) : value;
});
Vue.filter("constantCase", function (value) {
  return value ? constantCase(value) : value;
});
Vue.filter("dotCase", function (value) {
  return value ? dotCase(value) : value;
});
Vue.filter("headerCase", function (value) {
  return value ? headerCase(value) : value;
});
Vue.filter("noCase", function (value) {
  return value ? noCase(value) : value;
});
Vue.filter("paramCase", function (value) {
  return value ? paramCase(value) : value;
});
Vue.filter("pascalCase", function (value) {
  return value ? pascalCase(value) : value;
});
Vue.filter("pathCase", function (value) {
  return value ? pathCase(value) : value;
});
Vue.filter("sentenceCase", function (value) {
  return value ? sentenceCase(value) : value;
});
Vue.filter("snakeCase", function (value) {
  return value ? snakeCase(value) : value;
});
Vue.filter("plural", function (value) {
  return value ? plural(value) : value;
});
Vue.filter("singular", function (value) {
  return value ? singular(value) : value;
});
Vue.filter("lowercase", function (value) {
  return value ? value.toLowerCase() : value;
});
Vue.filter("uppercase", function (value) {
  return value ? value.toUpperCase() : value;
});

Vue.filter("initials", function (value, includeSecond = false) {
  const words = value.trim().split(" ");

  var firstWordLetter = "";
  var lastWordLetter = "";

  if (
    words.length > 0 &&
    (words[0].trim().toLowerCase() == "the" ||
      words[0].trim().toLowerCase() == "a" ||
      words[0].trim().toLowerCase() == "an")
  )
    words.shift(); // remove the first word if its "a", "an" or "the"

  if (words.length > 0 && words[0].length > 0)
    firstWordLetter = words[0].substr(0, 1);

  if (includeSecond && words.length > 1 && words[words.length - 1].length > 0)
    lastWordLetter = words[words.length - 1].substr(0, 1);

  return firstWordLetter.toUpperCase() + lastWordLetter.toUpperCase();
});

Vue.filter("friendlyId", function (value) {
  if (!value || value.length == 0 || value.trim() == "") return value;

  return value.toFriendlyId();
});

Vue.filter("nextlevelFriendlyId", function (value) {
  if (!value || value.length == 0 || value.trim() == "") return value;

  return value.toFriendlyId();
});

Vue.filter("socialSecurityNumber", function (value) {
  if (value && (value.length == 9 || value.length == 11)) {
    value = value.replace(/-/g, "").trim();
    return (
      value.substr(0, 3) + "-" + value.substr(3, 2) + "-" + value.substr(5, 4)
    );
  }

  return value;
});

Vue.filter("employerIdentificationNumber", function (value) {
  if (value && value.length == 9) {
    value = value.replace(/-/g, "").trim();
    return value.substr(0, 2) + "-" + value.substr(2, 7);
  }

  return value;
});

Vue.filter("maskedSocialSecurityNumber", function (value) {
  if (value && (value.length == 9 || value.length == 11)) {
    value = value.replace(/-/g, "").trim();

    return "XXX-XX-" + value.substr(5, 4);
  }

  return value;
});

Vue.filter("phoneNumber", function (value) {
  if (value == null || value.trim() == "") return value;

  var formatted = value;
  if (formatted.startsWith("+1")) formatted = formatted.substr(2);

  var numbersOnly = formatted.replace(/[^\d]/g, "");

  formatted = "(" + numbersOnly.substr(0, 3);

  if (numbersOnly.length > 3) {
    formatted += ") " + numbersOnly.substr(3, 3);
  }

  if (numbersOnly.length > 6) {
    formatted += "-" + numbersOnly.substr(6, 4);
  }

  if (formatted.length == 14) return formatted;

  return value;
});

// Output of this filter is rendered with v-html, so the source value must be HTML-encoded
// before the highlight markup is added or attacker-supplied names become stored XSS.
// lodash's escape covers exactly the same five characters.
Vue.filter("highlightQuery", function (value, searchTerms) {
  if (
    value == null ||
    !value.trim ||
    value.trim() == "" ||
    searchTerms == null ||
    !searchTerms.trim ||
    searchTerms.trim() == ""
  )
    return value == null ? value : escapeHtml(String(value));

  var rawValue = String(value);

  // Each term carries its own pair of word boundaries. Joining bare terms would put a
  // trailing \b only on the last one, so "jane doe" would match "Jane" inside "Janet".
  var terms = searchTerms
    .replace(/[ ]{2,}/gi, " ") // get rid of any duplicate spaces
    .trim()
    .split(" ")
    .map(function (term) {
      return "\\b" + term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "\\b";
    });

  var regex = new RegExp("(" + terms.join("|") + ")", "gi");

  // Matching runs against the raw value and each segment is escaped as the string is
  // rebuilt. Highlighting the escaped string instead would let a term such as "amp" or
  // "quot" match inside an entity and break it apart.
  var result = "";
  var lastIndex = 0;
  var match;

  while ((match = regex.exec(rawValue)) !== null) {
    if (match[0] === "") {
      regex.lastIndex++;
      continue;
    }

    result += escapeHtml(rawValue.slice(lastIndex, match.index));
    result +=
      "<span class='highlight-search-term'>" + escapeHtml(match[0]) + "</span>";
    lastIndex = match.index + match[0].length;
  }

  return result + escapeHtml(rawValue.slice(lastIndex));
});

Vue.filter("enumDisplay", function (value) {
  if (value) {
    const items = window.$cache.get("LookupData_EnumDisplay", []);
    let enumDisplay = items.find((x) => x.enumValue === value);
    let foundDisplay = enumDisplay?.displayName;
    return foundDisplay ? foundDisplay : value.toCapitalCase();
  } else {
    return "";
  }
});

String.camelCase = camelCase;
String.titleCase = capitalCase;
String.capitalCase = capitalCase;
String.constantCase = constantCase;
String.dotCase = dotCase;
String.headerCase = headerCase;
String.noCase = noCase;
String.paramCase = paramCase;
String.pascalCase = pascalCase;
String.pathCase = pathCase;
String.sentenceCase = sentenceCase;
String.snakeCase = snakeCase;
String.plural = plural;
String.singular = singular;

String.prototype.toCamelCase = function () {
  return String.camelCase(this);
};
String.prototype.toTitleCase = function () {
  return String.titleCase(this);
};
String.prototype.toCapitalCase = function () {
  return String.capitalCase(this);
};
String.prototype.toConstantCase = function () {
  return String.constantCase(this);
};
String.prototype.toDotCase = function () {
  return String.dotCase(this);
};
String.prototype.toHeaderCase = function () {
  return String.headerCase(this);
};
String.prototype.toNoCase = function () {
  return String.noCase(this);
};
String.prototype.toParamCase = function () {
  return String.paramCase(this);
};
String.prototype.toPascalCase = function () {
  return String.pascalCase(this);
};
String.prototype.toPathCase = function () {
  return String.pathCase(this);
};
String.prototype.toSentenceCase = function () {
  return String.sentenceCase(this);
};
String.prototype.toSnakeCase = function () {
  return String.snakeCase(this);
};
String.prototype.toPlural = function () {
  return String.plural(this);
};
String.prototype.toSingular = function () {
  return String.singular(this);
};

String.prototype.toFriendlyId = function () {
  var value = this;

  if (!value || value.length == 0 || value.trim() == "") return value;

  if (value.length >= 15) {
    value = value.replace(/-/g, "");
    var returnValue =
      value.substr(0, 2) +
      "-" +
      value.substr(2, 6) +
      "-" +
      value.substr(8, 3) +
      "-" +
      value.substr(11, 4);

    if (value.length > 15) {
      returnValue += "-" + value.substr(15);
    }

    return returnValue;
  } else if (value.length >= 12) {
    // NextLevel IRA Relius Plan
    value = value.replace("-", "");
    return (
      value.substr(0, 2) +
      "-" +
      value.substr(2, 4) +
      "-" +
      value.substr(6, 3) +
      "-" +
      value.substr(9)
    );
  }

  return value;
};
